import { clsx } from '@alexis/helpers/clsx';
import { translateNumber } from '@alexis/helpers/translation';
import React, { useEffect, useState } from 'react';

import { ARABIC_SYMBOLS } from '@constants/currency';

import { checkIsSymbolFontLoaded, getCurrencySymbolFontFamily } from '@helpers/currency';
import { isBigValueCurrency } from '@helpers/price';

import { Currency } from '@wegoTypes/currency';

import styles from '../styles/components/Price.module.scss';

interface PriceProps {
  className?: string;
  currency: Currency;
  exchangeRate: ExchangeRate;
  isInline?: boolean;
  isStrikethrough?: boolean;
  locale: string;
  price: Price;
  showDecimal?: boolean;
  style?: React.CSSProperties;
  // dataPw is the id that is used for automation testing by playwright
  dataPw?: string;
  id?: string;
  googleCrawlerForBowEnabled?: boolean;
  isRefund?: boolean;
}

const Price: React.FC<PriceProps> = ({
  className,
  currency,
  exchangeRate,
  isInline,
  isStrikethrough,
  locale,
  price,
  showDecimal,
  style,
  dataPw,
  id,
  googleCrawlerForBowEnabled,
  isRefund,
}) => {
  const [showAdditionalZeroPlaceholder, setShowAdditionalZeroPlaceholder] =
    useState<boolean>(false);
  const [amountDisplay, setAmountDisplay] = useState<string>('');

  const [isNegative, setIsNegative] = useState<boolean>(false);

  const symbolFontFamily = getCurrencySymbolFontFamily(currency.code);

  const isSymbolFontLoaded = checkIsSymbolFontLoaded(currency.code);

  const shouldShowArabicSymbol = !!(symbolFontFamily && isSymbolFontLoaded);

  useEffect(() => {
    const currencyDecimals =
      currency.numberOfDecimals !== undefined ? currency.numberOfDecimals : 2;
    const decimalPoints: number = showDecimal ? currencyDecimals : 0;
    const isPriceCurrencySameAsSelectedCurrency = price.currencyCode === currency.code;

    let amount = isPriceCurrencySameAsSelectedCurrency
      ? price.amount
      : price.amountUsd * exchangeRate.rate;

    if (amount < 0) {
      amount = amount * -1;
      setIsNegative(true);
    } else {
      setIsNegative(false);
    }

    if (isBigValueCurrency(currency.code) && amount > 99999) {
      amount = amount / 1000;
      setShowAdditionalZeroPlaceholder(true);
    } else {
      setShowAdditionalZeroPlaceholder(false);
    }

    const stringAmount =
      isBigValueCurrency(currency.code) || amount > 99999
        ? amount.toFixed(0)
        : amount.toFixed(decimalPoints);

    const stringAmountLength = stringAmount.length;

    let modValue: number = 0;

    if (!(isBigValueCurrency(currency.code || '') || amount > 99999) && decimalPoints > 0) {
      modValue = stringAmountLength - 3 > 3 ? stringAmountLength % 3 : 0;
    } else {
      modValue = stringAmountLength > 3 ? stringAmountLength % 3 : 0;
    }

    setAmountDisplay(
      translateNumber(
        `${
          !!modValue ? `${stringAmount.slice(0, modValue)}${currency.separator}` : ''
        }${stringAmount.slice(modValue).replace(/(\d{3})(?=\d)/g, `$1${currency.separator}`)}`,
        locale === 'fa',
      ),
    );
  }, [price, locale, currency, exchangeRate, showDecimal]);

  const symbolPlaceholderView = (isInline: boolean) => {
    if (shouldShowArabicSymbol) {
      return (
        <span className={`custom-font-${symbolFontFamily}`}>
          {/* The trailing white space is required */}
          {ARABIC_SYMBOLS[currency.code]}{' '}
        </span>
      );
    }

    if (isInline) {
      return <span className={styles.symbol}>{currency.symbol}</span>;
    }
    return <sup className={styles.symbol}>{currency.symbol}</sup>;
  };

  return (
    <div
      data-pw={dataPw}
      data-testid='price'
      className={clsx(styles.container, isStrikethrough && styles.strikethrough, className)}
      style={style}
      id={id}
    >
      {isNegative || isRefund ? <>-&nbsp;</> : null}

      {currency.symbolFirst ? symbolPlaceholderView(!!isInline) : null}

      <span
        id={googleCrawlerForBowEnabled ? 'totalPrice' : undefined}
        data-currency-code={googleCrawlerForBowEnabled ? currency.code : undefined}
        data-price-accuracy={googleCrawlerForBowEnabled ? (showDecimal ? 2 : 0) : undefined}
        className={clsx(
          styles.amount,
          !showAdditionalZeroPlaceholder && !currency.symbolFirst && styles.withRightMargin,
          shouldShowArabicSymbol && styles.noMargin,
        )}
      >
        {amountDisplay}
      </span>

      {showAdditionalZeroPlaceholder ? (
        isInline ? (
          <span className={clsx(!currency.symbolFirst && styles.withRightMargin)}>
            {translateNumber(`${currency.separator}000`, locale === 'fa')}
          </span>
        ) : (
          <sup>{translateNumber(`${currency.separator}000`, locale === 'fa')}</sup>
        )
      ) : null}

      {!currency.symbolFirst ? symbolPlaceholderView(!!isInline) : null}
    </div>
  );
};

export default Price;
